import { FC, useState, useEffect, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { InputAdornment, IconButton } from '@material-ui/core'
import { TextFieldProps } from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import styled from 'styled-components'
import {
  useCoverLetterScreen_SearchForm_CoverLetterOrganizationListLazyQuery,
  CoverLetterStatus,
} from 'generated/graphql'
import { getCoverLetterTypeFromTab } from '@app.feature/coverletter/modules/getCoverLetterTypeFromTab'
import TextField from 'components/custom-material-ui/TextField'
import { FormData } from 'screens/CoverLetterScreen/types'
import { useSearchStateContext } from 'screens/CoverLetterScreen/useSearchState'
import CloseIcon from 'svgIcons/Close'
import CompanyIcon from 'svgIcons/Company'
import { TShowAutoCompleteType } from './SearchFormMobileMui'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    minHeight: 50,
    marginTop: 10,
  },
  textField: {
    marginRight: 5,
    flex: 1,
    marginBottom: 10,
  },
  fieldIcon: {
    marginLeft: 16,
    height: 16,
    width: 16,
    color: '#333333',
  },

  autoCompletePaper: {
    border: '1px solid #ced4da',
  },
  closeIcon: {
    width: 10,
    height: 10,
  },
  autocompleteOption: {
    // nowarp for samsung/naver browser
    whiteSpace: 'nowrap',
  },
}))

interface OrganizationNameFieldMobileMuiProps {
  InputProps?: TextFieldProps['InputProps']
  className?: string
  showAutoComplete: TShowAutoCompleteType
  setShowAutoComplete: React.Dispatch<
    React.SetStateAction<TShowAutoCompleteType>
  >
}

const OrganizationNameFieldMobileMui: FC<
  OrganizationNameFieldMobileMuiProps
> = (props) => {
  const classes = useStyles()
  const { InputProps: textFieldInputProps, className } = props
  const { tabState } = useSearchStateContext()

  const { register, watch, setValue, formState } = useFormContext<FormData>()

  const watchOrganizationName = watch('organizationName')

  const { isSubmitting, touchedFields } = formState
  const orgTouched = touchedFields.organizationName

  useEffect(() => {
    if (!orgTouched) {
      props.setShowAutoComplete('')
    }
  }, [orgTouched])

  const [organizationList, setOrganizationList] = useState<
    any[] | null | undefined
  >(null)

  const [
    getOrganizationList,
    { data: organizationData, loading: orgNameLoading, refetch, called },
  ] = useCoverLetterScreen_SearchForm_CoverLetterOrganizationListLazyQuery()

  const getOrganizationName = (value: any) => {
    if (called) {
      refetch({
        input: {
          query: value,
          status: CoverLetterStatus.PUBLISHED,
          types: getCoverLetterTypeFromTab(tabState),
        },
      })
    } else {
      getOrganizationList({
        variables: {
          input: {
            query: value,
            status: CoverLetterStatus.PUBLISHED,
            types: getCoverLetterTypeFromTab(tabState),
          },
        },
      })
    }
    props.setShowAutoComplete('organizationName')
  }

  const handleClearOrganizationNameField = () => {
    setValue('organizationName', '', {
      shouldDirty: true,
    })
    props.setShowAutoComplete('')
  }

  // 자동완성 리스트
  useEffect(() => {
    if (organizationData?.coverLetterOrganizationList) {
      setOrganizationList(organizationData?.coverLetterOrganizationList)
    }
  }, [organizationData, organizationList])

  const handleChange = useCallback(
    (e: any) => {
      setValue('organizationName', e.target.value, {
        shouldDirty: true,
      })
    },
    [setValue],
  )

  const handleOnChange = (_e, value: FormData) => {
    if (value) {
      setValue('organizationName', value.organizationName, {
        shouldDirty: true,
      })
      props.setShowAutoComplete('')
    }
  }

  useEffect(() => {
    register('organizationName')
  }, [register])

  const autoCompleteClasses = {
    root: className,
    paper: classes.autoCompletePaper,
    option: classes.autocompleteOption,
  }

  return (
    <Autocomplete
      open={props.showAutoComplete === 'organizationName'}
      onClose={() => {
        props.setShowAutoComplete('')
      }}
      disabled={isSubmitting}
      id="cover-letter-autocomplete-organization-name"
      getOptionLabel={(org) => org.organizationName}
      options={organizationList || []}
      loading={orgNameLoading}
      classes={autoCompleteClasses}
      noOptionsText="일치하는 기업이 없습니다."
      loadingText="불러오는중..."
      onChange={handleOnChange}
      inputValue={watchOrganizationName}
      filterOptions={(options) => options}
      // TODO: Update MUI first
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderInput={({ InputLabelProps, ...params }) => {
        return (
          <StyledWrapper>
            <TextField
              {...params}
              onChange={handleChange}
              fullWidth
              name="organizationName"
              type="search"
              className={classes.textField}
              placeholder="기업명"
              onDebounceChange={getOrganizationName}
              debounceDelayMS={orgTouched ? 500 : 0}
              disabled={isSubmitting}
              InputProps={{
                ...params.InputProps,
                ...textFieldInputProps,
                inputProps: {
                  ...params?.inputProps,
                  ...textFieldInputProps?.inputProps,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <CompanyIcon className={classes.fieldIcon} />
                  </InputAdornment>
                ),
                endAdornment: watchOrganizationName ? (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearOrganizationNameField}>
                      <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </StyledWrapper>
        )
      }}
    />
  )
}

export default OrganizationNameFieldMobileMui

const StyledWrapper = styled.div`
  input {
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }
  }
`
